import React, { useState } from 'react';
import { Section } from '../ui/Section';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Moon, Wind, Sparkles, Heart, Clock, DollarSign, Zap, Calendar, Lock, AlertTriangle, ChevronDown } from 'lucide-react';
interface RecipeData {
  icon: React.ElementType;
  color: string;
  title: string;
  subtitle: string;
  empathy: string;
  recipeName: string;
  stats: {
    prep: string;
    cost: string;
    relief: string;
    duration: string;
  };
  ingredients: string[];
  steps: string[];
  dosage: {
    headers: string[];
    rows: string[][];
  };
  note: string;
  locked: string[];
}
function RecipeAccordion({
  recipe,
  isOpen,
  onToggle,
  index
}: {
  recipe: RecipeData;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}) {
  const Icon = recipe.icon;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-30px'
  }} transition={{
    delay: index * 0.08
  }} className="bg-white rounded-xl shadow-md overflow-hidden border-l-8 mb-3" style={{
    borderLeftColor: recipe.color
  }} data-id="element-176">
      {/* Accordion Button */}
      <button onClick={onToggle} className="w-full flex items-center gap-4 p-4 md:p-5 text-left hover:bg-gray-50/50 transition-colors cursor-pointer" data-id="element-177">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
        backgroundColor: `${recipe.color}15`
      }} data-id="element-178">
          <Icon className="w-5 h-5" style={{
          color: recipe.color
        }} data-id="element-179" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-180">
          <h3 className="text-base md:text-lg font-bold text-[#2C1810] leading-tight" data-id="element-181">
            {recipe.title}
          </h3>
          <p className="text-sm text-gray-500 mt-0.5 truncate" data-id="element-182">
            {recipe.subtitle}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-183">
          <span className="text-xs font-bold px-2 py-1 rounded-full hidden md:inline-block" style={{
          backgroundColor: `${recipe.color}15`,
          color: recipe.color
        }} data-id="element-184">
            {recipe.stats.prep}
          </span>
          <motion.div animate={{
          rotate: isOpen ? 180 : 0
        }} transition={{
          duration: 0.3
        }} data-id="element-185">
            <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-186" />
          </motion.div>
        </div>
      </button>

      {/* Expandable Content */}
      <AnimatePresence data-id="element-187">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-188">
            <div className="px-4 md:px-6 pb-6 border-t border-gray-100" data-id="element-189">
              {/* Empathy */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 italic text-gray-700 mt-4 mb-6 text-sm" data-id="element-190">
                <span className="font-bold text-[#2C1810] not-italic" data-id="element-191">
                  ¿Te suena esto?
                </span>{' '}
                {recipe.empathy}
              </div>

              {/* Recipe Box */}
              <div className="bg-[#FDF8F0] rounded-xl border-2 border-dashed border-[#2C1810]/20 p-4 md:p-6" data-id="element-192">
                <div className="flex items-center gap-2 mb-4" data-id="element-193">
                  <span className="text-xl" data-id="element-194">🧪</span>
                  <h4 className="text-lg font-bold text-[#2C1810]" data-id="element-195">
                    MICRO RECETA: {recipe.recipeName}
                  </h4>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5" data-id="element-196">
                  {[{
                icon: Clock,
                label: 'Preparación',
                value: recipe.stats.prep
              }, {
                icon: DollarSign,
                label: 'Costo',
                value: recipe.stats.cost
              }, {
                icon: Zap,
                label: 'Alivio',
                value: recipe.stats.relief
              }, {
                icon: Calendar,
                label: 'Duración',
                value: recipe.stats.duration
              }].map((stat, i) => <div key={i} className="bg-white p-2 rounded border border-gray-200 text-center" data-id="element-197">
                      <stat.icon className="w-3.5 h-3.5 text-gray-400 mx-auto mb-0.5" data-id="element-198" />
                      <p className="text-[10px] text-gray-500 uppercase" data-id="element-199">
                        {stat.label}
                      </p>
                      <p className="font-bold text-[#2C1810] text-xs" data-id="element-200">
                        {stat.value}
                      </p>
                    </div>)}
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-5" data-id="element-201">
                  {/* Ingredients */}
                  <div data-id="element-202">
                    <h5 className="font-bold text-[#2D5016] mb-2 uppercase text-xs tracking-wider border-b border-[#2D5016]/20 pb-1" data-id="element-203">
                      Ingredientes
                    </h5>
                    <ul className="space-y-1.5" data-id="element-204">
                      {recipe.ingredients.map((ing, i) => <li key={i} className="flex items-start gap-2 text-sm" data-id="element-205">
                          <span className="text-[#D4A017] mt-0.5" data-id="element-206">•</span>
                          <span className="text-[#2C1810]" data-id="element-207">{ing}</span>
                        </li>)}
                    </ul>
                  </div>

                  {/* Preparation */}
                  <div data-id="element-208">
                    <h5 className="font-bold text-[#2D5016] mb-2 uppercase text-xs tracking-wider border-b border-[#2D5016]/20 pb-1" data-id="element-209">
                      Preparación
                    </h5>
                    <ol className="space-y-1.5" data-id="element-210">
                      {recipe.steps.map((step, i) => <li key={i} className="flex items-start gap-2 text-sm" data-id="element-211">
                          <span className="font-bold text-[#2D5016] min-w-[1.2rem]" data-id="element-212">
                            {i + 1}.
                          </span>
                          <span className="text-[#2C1810]" data-id="element-213">{step}</span>
                        </li>)}
                    </ol>
                  </div>
                </div>

                {/* Dosage Table */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-5" data-id="element-214">
                  <div className="bg-[#2C1810] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider" data-id="element-215">
                    Dosificación por peso
                  </div>
                  <div className="overflow-x-auto" data-id="element-216">
                    <table className="w-full text-sm text-left" data-id="element-217">
                      <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200" data-id="element-218">
                        <tr data-id="element-219">
                          {recipe.dosage.headers.map((h, i) => <th key={i} className="px-3 py-1.5 text-xs" data-id="element-220">
                              {h}
                            </th>)}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100" data-id="element-221">
                        {recipe.dosage.rows.map((row, i) => <tr key={i} data-id="element-222">
                            {row.map((cell, j) => <td key={j} className={`px-3 py-1.5 text-xs ${j === 0 ? 'font-bold text-[#2C1810]' : 'text-gray-600'}`} data-id="element-223">
                                {cell}
                              </td>)}
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Warning Note */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex gap-2 text-xs text-yellow-800" data-id="element-224">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-225" />
                  <p className="italic" data-id="element-226">{recipe.note}</p>
                </div>
              </div>

              {/* Locked Section */}
              <div className="bg-[#2C1810] rounded-xl p-4 mt-4 text-white/90" data-id="element-227">
                <div className="flex items-center gap-2 mb-3 text-[#D4A017] font-bold uppercase tracking-wider text-xs" data-id="element-228">
                  <Lock className="w-3.5 h-3.5" data-id="element-229" />
                  Solo en el botiquín completo:
                </div>
                <ul className="space-y-1.5" data-id="element-230">
                  {recipe.locked.map((item, i) => <li key={i} className="flex items-start gap-2 text-xs opacity-80" data-id="element-231">
                      <span className="text-red-400" data-id="element-232">❌</span>
                      <span data-id="element-233">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function MicroRecipes() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const recipes: RecipeData[] = [{
    icon: Flame,
    color: '#EF4444',
    title: 'Dolor #1: Inflamación de Rodillas y Espalda',
    subtitle: 'El dolor que aparece a las 7pm y arruina tu noche',
    empathy: 'Llegas a casa, intentas agacharte y un rayo de fuego te sube por la espalda. Tus rodillas crujen. No puedes jugar con tus hijos. No puedes cocinar. El ibuprofeno ya no funciona como antes.',
    recipeName: 'Crema Árnica Express',
    stats: {
      prep: '10 min',
      cost: '< $3',
      relief: '20-30 min',
      duration: '1 mes'
    },
    ingredients: ['50ml aceite de coco', '2 cucharadas árnica seca (mercado local)', '1 cucharadita cúrcuma en polvo', 'Pizca de pimienta negra (activa cúrcuma)'],
    steps: ['Calienta aceite de coco a baño maría (no hervir).', 'Agrega árnica y cúrcuma.', 'Revuelve 10 minutos a fuego bajo.', 'Cuela, enfría en frasco vidrio.', 'Aplica masaje circular 5 minutos en zona afectada.'],
    dosage: {
      headers: ['Tu Peso', 'Cantidad Aplicar', 'Frecuencia'],
      rows: [['50-70kg', '8-10ml', '2 veces/día'], ['70-90kg', '12-15ml', '2-3 veces/día'], ['90+kg', '18-20ml', '3 veces/día']]
    },
    note: 'Esto es solo 1 de las 8 recetas antiinflamatorias del Capítulo 2. Adentro encuentras 7 más, incluyendo compresas, bálsamos y tés específicos para cada tipo de dolor articular.',
    locked: ['Receta rotación día 3 (evita tolerancia)', 'Dosificación exacta para dolor crónico vs agudo', 'Combinación con té interno para resultados 2x más rápidos', 'Framework RAPIDO completo (alivio en 30 minutos)']
  }, {
    icon: Moon,
    color: '#6366F1',
    title: 'Dolor #2: Insomnio y Despertares 3AM',
    subtitle: 'El cansancio que no desaparece aunque "duermas"',
    empathy: 'Son las 3:17am. Estás despierta otra vez. Corazón acelerado, mente activa, cuerpo agotado pero sin poder dormir. Mañana laboral espera. Y tú llevas 6 meses así.',
    recipeName: 'Té Valeriana-Manzanilla Profundo',
    stats: {
      prep: '8 min',
      cost: '< $2',
      relief: '45-60 min',
      duration: 'Noche 1-3'
    },
    ingredients: ['250ml agua filtrada', '2.5g valeriana seca (herbolaria)', '2g manzanilla', '1 cucharadita miel pura', '3 gotas jugo limón'],
    steps: ['Hierve agua hasta 90°C (no 100° — destruye activos).', 'Agrega valeriana primero, infusiona 7 minutos.', 'Agrega manzanilla, 3 minutos más.', 'Cuela, añade miel y limón.', 'Bebe 45-60 minutos antes de dormir.'],
    dosage: {
      headers: ['Tu Peso', 'Valeriana', 'Manzanilla', 'Frecuencia'],
      rows: [['50-70kg', '2g', '1.5g', '1 taza/noche'], ['70-90kg', '2.5g', '2g', '1 taza/noche'], ['90+kg', '3g', '2.5g', '1-2 tazas/noche']]
    },
    note: 'Esto es solo 1 de las 7 recetas bedtime del Capítulo 3. Adentro encuentras el Ciclo Sueño Herbal 3 Pasos completo que combina sedante + antiinflamatorio para duplicar horas de sueño.',
    locked: ['Bálsamo tópico lavanda-pasiflora (potencia efecto 2x)', 'Rotación anti-tolerancia para semana 2', 'Receta específica para insomnio hormonal vs insomnio estrés', 'Spray habitación que prepara ambiente sueño profundo']
  }, {
    icon: Wind,
    color: '#14B8A6',
    title: 'Dolor #3: Hinchazón y Digestión Pesada',
    subtitle: 'El estómago globo que no te deja dormir ni moverte',
    empathy: 'Terminas de cenar y en 20 minutos pareces embarazada. Gases, acidez, pesadez. No puedes acostarte cómoda. Tu estómago suena. Y pasa cada noche sin importar qué comas.',
    recipeName: 'Gotas Digestivas Menta-Ajo',
    stats: {
      prep: '5 min',
      cost: '< $2',
      relief: '20-40 min',
      duration: '2 semanas'
    },
    ingredients: ['50ml agua tibia', '4 hojas menta fresca', '1 diente ajo machacado', 'Jugo de medio limón', 'Pizca sal marina'],
    steps: ['Machaca ajo hasta pasta fina.', 'Mezcla con agua tibia (no caliente).', 'Agrega menta machacada, limón y sal.', 'Revuelve 2 minutos.', 'Toma 2-3 cucharadas post-comida.'],
    dosage: {
      headers: ['Tu Peso', 'Cantidad', 'Momento', 'Frecuencia'],
      rows: [['50-70kg', '2 cdas', '20min post', '2 veces/día'], ['70-90kg', '3 cdas', '20min post', '2-3 veces/día'], ['90+kg', '4 cdas', '20min post', '3 veces/día']]
    },
    note: 'Esto es solo 1 de las 6 recetas digestivas del Capítulo 4. Adentro encuentras el Método 4F completo: pre-comida, durante, post y mantenimiento diario.',
    locked: ['Té boldo preventivo pre-comida (elimina hinchazón antes que empiece)', 'Jugo papaya-piña enzimas (disuelve grasas pesadas)', 'Receta específica para intestino irritable vs acidez vs gas', 'Plan digestivo semanal completo']
  }, {
    icon: Sparkles,
    color: '#F59E0B',
    title: 'Dolor #4: Acné Hormonal y Piel Inflamada',
    subtitle: 'Los granos que regresan cada mes sin importar tu edad',
    empathy: 'Tienes 30, 35, 40 años y sigues con granos. Profundos, dolorosos, en barbilla y mandíbula. Justo antes del ciclo. Cremas de farmacia los secan pero regresan. Estás harta de cubrirlos con maquillaje.',
    recipeName: 'Mascarilla Cúrcuma-Miel Anti-Hormonal',
    stats: {
      prep: '3 min',
      cost: '< $2',
      relief: '24-48 horas',
      duration: '2-3x/sem'
    },
    ingredients: ['2 cucharaditas cúrcuma en polvo', '1 cucharada miel pura cruda', '3-4 gotas agua destilada', 'Pizca canela (activa circulación)'],
    steps: ['Mezcla cúrcuma + miel hasta pasta uniforme.', 'Agrega agua gota a gota (no líquida, debe ser pasta).', 'Añade canela al final.', 'Aplica capa delgada en zonas afectadas.', 'Deja 15 minutos. Retira con agua tibia.'],
    dosage: {
      headers: ['Tipo Acné', 'Frecuencia', 'Tiempo', 'Resultado'],
      rows: [['Leve', '2x semana', '10 min', 'Semana 1'], ['Moderado', '3x semana', '15 min', 'Semana 2'], ['Severo', 'Diario', '20 min', 'Semana 2-3']]
    },
    note: 'Esto es solo 1 de las 8 recetas de piel del Capítulo 5. Adentro encuentras el Protocolo Piel 5 Pasos completo + recetas internas para atacar raíz hormonal.',
    locked: ['Té ortiga-lavanda interno (ataca raíz hormonal)', 'Ungüento propóleo para cicatrices (aclara mes 2)', 'Rotación semanal mascarillas (evita tolerancia cutánea)', 'Protocolo combinado piel + hormonal (resultados 3x más rápidos)']
  }, {
    icon: Heart,
    color: '#E11D48',
    title: 'Dolor #5: Colesterol Alto y Energía Baja',
    subtitle: 'Los números que suben solos y el cansancio que no se va',
    empathy: 'Analítica: colesterol 248. Tu doctor ya tiene la receta lista: "pastilla de por vida". Tienes 42 años. Sin síntomas claros. Pero cansado todo el tiempo. Después de comer necesitas siesta. Tu energía post-30 desapareció.',
    recipeName: 'Té Boldo-Diente León Hígado Limpio',
    stats: {
      prep: '8 min',
      cost: '< $2',
      relief: 'Semana 1-2',
      duration: 'Mes 1-2'
    },
    ingredients: ['250ml agua filtrada', '2g boldo seco (herbolaria)', '1.5g diente de león seco', 'Jugo de 1 limón', '1 cucharadita miel'],
    steps: ['Hierve agua, baja fuego.', 'Agrega boldo primero, 5 minutos.', 'Agrega diente de león, 3 minutos más.', 'Cuela completamente.', 'Añade limón y miel. Toma en ayunas.'],
    dosage: {
      headers: ['Tu Peso', 'Boldo', 'Diente León', 'Frecuencia'],
      rows: [['50-70kg', '1.5g', '1g', '1 taza/ayunas'], ['70-90kg', '2g', '1.5g', '1 taza/ayunas'], ['90+kg', '2.5g', '2g', '1-2 tazas/ayunas']]
    },
    note: 'Esto es solo 1 de las 9 recetas cardio del Capítulo 7. Adentro encuentras el Protocolo Cardio 4 Pasos completo: Limpiar-Bajar-Proteger-Sostener.',
    locked: ['Arroz rojo polvo (estatina natural sin efectos secundarios)', 'Aceite oliva-limón ayunas (sube HDL colesterol bueno)', 'Semillas lino (fibra que atrapa colesterol en intestino)', 'Protocolo completo 30 días con analítica seguimiento']
  }];
  return <Section className="bg-[#FDF8F0]" data-id="element-234">
      <div className="text-center mb-10" data-id="element-235">
        <h2 className="text-3xl md:text-4xl font-bold text-[#2C1810] mb-4" data-id="element-236">
          "Una Probadita De Lo Que Encuentras Adentro..."
        </h2>
        <p className="text-lg text-[#2C1810]/80 max-w-2xl mx-auto" data-id="element-237">
          Estas son solo 5 de las 40+ recetas dosificadas que obtienes hoy. Cada
          una preparada en menos de 15 minutos con ingredientes de tu mercado
          local.
        </p>
        <p className="text-sm text-gray-500 mt-3" data-id="element-238">
          👇 Toca cada receta para ver los detalles
        </p>
      </div>

      <div className="max-w-3xl mx-auto" data-id="element-239">
        {recipes.map((recipe, index) => <RecipeAccordion key={index} recipe={recipe} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? null : index)} index={index} data-id="element-240" />)}
      </div>

      <div className="text-center mt-10 mb-8" data-id="element-241">
        <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-[#2D5016] inline-block max-w-2xl" data-id="element-242">
          <h3 className="text-xl font-bold text-[#2D5016] mb-2" data-id="element-243">
            "Estas 5 recetas son solo una probadita."
          </h3>
          <p className="text-[#2C1810] font-medium" data-id="element-244">
            "Adentro del botiquín completo encuentras 40+ más."
          </p>
        </div>
      </div>
    </Section>;
}